<template>
  <div class="locations" :class="cssClass">
    <h3
      class="margin-0"
      :class="{
        'text-danger': store.error && store.requireFields?.includes('locations'),
      }"
    >
      {{ t('measurement-location').toUpperCase() }}

      <span v-if="cssClass !== 'vms' && sortedLocations.length" class="select-all-text">
        {{ allSelected ? t('unmark') : t('mark') }} {{ t('all').toLowerCase() }}
      </span>

      <input-checkbox
        v-if="cssClass !== 'vms' && sortedLocations.length"
        field-name="selectAll"
        field-value=""
        :checkedCondition="allSelected"
        @clicked="setAllFilter()"
      />
    </h3>

    <table class="table">
      <thead>
        <tr>
          <th class="sign" scope="col">></th>
          <th scope="col">Id</th>
          <th scope="col">{{ t('location-name') }}</th>
          <th scope="col" class="path-number">{{ t('road-no') }}</th>
          <th scope="col">{{ t('chainage') }}</th>
          <th scope="col">{{ t('select') }}</th>
        </tr>
      </thead>

      <tbody v-if="sortedLocations.length">
        <tr v-for="loc in sortedLocations" :key="loc.id">
          <td scope="row">
            <img :src="isActive(loc) ? availableIcon : unavailableIcon" alt="Status" />
          </td>
          <td>{{ loc.id }}</td>
          <td>{{ loc.shortName }}</td>
          <td>{{ loc.roadNumber }}</td>
          <td>{{ loc.chainage }}</td>
          <td class="check">
            <input-checkbox
              :field-name="String(loc.id)"
              field-value=""
              :checkedCondition="isChecked(loc.id)"
              @clicked="setFilter(loc.id)"
            />
          </td>
        </tr>
      </tbody>

      <tbody v-else>
        <tr>
          <td colspan="6">
            <p class="no-data center-aligned">{{ t('no-data') }}</p>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import InputCheckbox from "../InputCheckbox.vue";
import availableIcon from "@/assets/icons/dostepny.png";
import unavailableIcon from "@/assets/icons/niedostepny.png";

export default {
  name: "LocationList",
  components: { InputCheckbox },
  props: {
    store: { required: true },
    cssClass: String,
    // próg „świeżości” w minutach (domyślnie 30)
    freshnessThresholdMinutes: { type: Number, default: 30 },
    // jeżeli lastVehicleTime bywa w sekundach unix, włącz normalizację
    lastTimeIsUnixSeconds: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      error: null,
      now: Date.now(),
      timer: null,
      availableIcon,
      unavailableIcon,
    };
  },
  created() {
    this.store.getLocations?.();
  },
  mounted() {
    // odśwież status co 60s, żeby ikonki aktualizowały się bez zmian w store
    this.timer = setInterval(() => {
      this.now = Date.now();
    }, 60 * 1000);
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  watch: {
    "store.locations"(newVal) {
      this.loading = !newVal || newVal.length === 0;
    },
  },
  computed: {
    sortedLocations() {
      const arr = Array.isArray(this.store.locations) ? this.store.locations : [];
      return [...arr].sort((a, b) => Number(a.id) - Number(b.id));
    },
    // Set dla szybszego i bezbłędnego sprawdzania (string vs number → zawsze number)
    selectedIdSet() {
      const ids = this.store?.filters?.locations || [];
      return new Set(ids.map((x) => Number(x)));
    },
    allSelected() {
      const locs = this.sortedLocations;
      if (!locs.length) return false;
      return locs.every((l) => this.selectedIdSet.has(Number(l.id)));
    },
    thresholdMs() {
      return this.freshnessThresholdMinutes * 60 * 1000;
    },
  },
  methods: {
    setFilter(id) {
      // dbaj o spójny typ id w store (polecam number)
      this.store.addItem?.("LocationList", Number(id));
    },
    isChecked(id) {
      return this.selectedIdSet.has(Number(id));
    },
    setAllFilter() {
      this.store.checkAllLocations?.();
    },
    parseTimestamp(value) {
      if (value == null) return NaN;
      // Obsługa Number, string ISO, unix s/ms
      if (typeof value === "number") {
        return this.lastTimeIsUnixSeconds ? value * 1000 : value;
      }
      const trimmed = String(value).trim();
      // liczba w stringu
      if (/^\d+$/.test(trimmed)) {
        const n = Number(trimmed);
        return this.lastTimeIsUnixSeconds ? n * 1000 : n;
      }
      // ISO
      const ms = Date.parse(trimmed);
      return Number.isNaN(ms) ? NaN : ms;
    },
    isActive(loc) {
      const ts = this.parseTimestamp(loc?.lastVehicleTime);
      if (Number.isNaN(ts)) return false; // brak danych → traktuj jako niedostępne
      return this.now - ts <= this.thresholdMs;
    },
  },
};
</script>

<style scoped>
.locations h3 {
  padding: 0.85rem 1rem 0.9rem 1rem;
  width: 100%;
  position: relative;
  border-right: 1px solid #bfbfbf;
  border-bottom: 1px solid #bfbfbf;
  border-top: 1px solid #bfbfbf;
}

.locations th, .locations td {
  text-align: center;
  vertical-align: middle; 
}

.locations tr {
  border-right: 1px solid #bfbfbf;
  border-bottom: 1px solid #bfbfbf;
}


.locations th, .locations td {
  font-weight: normal;
  font-size: 12px;
  padding: 0.75rem;
}

.locations td.check span {
  margin: 0 auto;
  margin-top: 0.1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem;
}

.sign {
  color: #eaeaea;
  font-weight: 1000;
  font-size: 15px;
}

.selectAll {
  position: absolute !important;
  bottom: 1px !important;
  left: 188px;
}

.select-all-text {
  font-size: 12px;
  position: absolute;
  right: 55px;
  top: 18px;
}

.path-number {
  white-space: nowrap;
}
</style>